import { addDays, today } from "../domain/dates.js";
import { esc } from "../domain/format.js";

let dateCalendar = null;

const calendarISO = (d) => d.toISOString().slice(0, 10);

function calendarDate(value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || "")) return null;
  const d = new Date(value + "T00:00:00Z");
  return Number.isFinite(+d) && calendarISO(d) === value ? d : null;
}

function calendarMonth(year, month) {
  const d = new Date(0);
  d.setUTCFullYear(year, month, 1);
  d.setUTCHours(0, 0, 0, 0);
  return d;
}

function calendarDays(year, month) {
  const first = calendarMonth(year, month),
    start = addDays(calendarISO(first), -first.getUTCDay());
  return Array.from({ length: 42 }, (_, i) => addDays(start, i));
}

function calendarAllowed(value, input) {
  return (
    !!calendarDate(value) &&
    value >= "0001-01-01" &&
    value <= "9999-12-31" &&
    (!input.min || value >= input.min) &&
    (!input.max || value <= input.max)
  );
}

function closeDateCalendar(restore = false) {
  const old = dateCalendar;
  dateCalendar = null;
  document.getElementById("date-calendar")?.remove();
  if (restore && old) {
    const field = document.getElementById(old.input.id) || old.input;
    if (field.isConnected) field.focus();
  }
}

function positionDateCalendar() {
  if (!dateCalendar) return;
  const panel = document.getElementById("date-calendar");
  if (!panel) return;
  const r = dateCalendar.input.getBoundingClientRect(),
    w = panel.offsetWidth || 304,
    h = panel.offsetHeight || 340;
  panel.style.left =
    Math.max(8, Math.min(r.left, window.innerWidth - w - 8)) + "px";
  panel.style.top =
    Math.max(
      8,
      r.bottom + 8 + h <= window.innerHeight ? r.bottom + 8 : r.top - h - 8,
    ) + "px";
}

function renderDateCalendar(focusDay = false) {
  if (!dateCalendar) return;
  const state = dateCalendar,
    input = state.input,
    year = state.month.getUTCFullYear(),
    month = state.month.getUTCMonth();
  let panel = document.getElementById("date-calendar");
  if (!panel) {
    panel = document.createElement("div");
    panel.id = "date-calendar";
    panel.className = "date-calendar";
    panel.setAttribute("role", "dialog");
    panel.setAttribute("aria-label", "Choose date");
    document.body.appendChild(panel);
  }
  const months = Array.from({ length: 12 }, (_, i) =>
      new Intl.DateTimeFormat("en-PH", {
        month: "long",
        timeZone: "UTC",
      }).format(calendarMonth(2026, i)),
    ),
    currentYear = +today().slice(0, 4),
    low = Math.max(1, Math.min(currentYear - 100, year - 10)),
    high = Math.min(9999, Math.max(currentYear + 20, year + 10)),
    selected = input.value;
  panel.innerHTML = `<div class="calendar-head"><button type="button" data-cal="prev" aria-label="Previous month"${year === 1 && month === 0 ? " disabled" : ""}>‹</button><select data-cal-month aria-label="Month">${months.map((m, i) => `<option value="${i}"${i === month ? " selected" : ""}>${m}</option>`).join("")}</select><select data-cal-year aria-label="Year">${Array.from(
    { length: high - low + 1 },
    (_, i) => low + i,
  )
    .map((y) => `<option${y === year ? " selected" : ""}>${y}</option>`)
    .join(
      "",
    )}</select><button type="button" data-cal="next" aria-label="Next month"${year === 9999 && month === 11 ? " disabled" : ""}>›</button></div><div class="calendar-week" aria-hidden="true">${["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => `<span>${d}</span>`).join("")}</div><div class="calendar-grid" role="group" aria-label="${months[month]} ${year}">${calendarDays(
    year,
    month,
  )
    .map((value) => {
      const date = new Date(value + "T00:00:00Z"),
        other = date.getUTCMonth() !== month,
        enabled = calendarAllowed(value, input),
        label = new Intl.DateTimeFormat("en-PH", {
          dateStyle: "full",
          timeZone: "UTC",
        }).format(date);
      return `<button type="button" data-cal-day="${value}" class="${other ? "outside " : ""}${value === selected ? "selected " : ""}${value === today() ? "is-today" : ""}" tabindex="${value === state.focus ? 0 : -1}" aria-label="${esc(label)}"${value === selected ? ' aria-pressed="true"' : ' aria-pressed="false"'}${value === today() ? ' aria-current="date"' : ""}${enabled ? "" : " disabled"}>${date.getUTCDate()}</button>`;
    })
    .join(
      "",
    )}</div><div class="calendar-footer"><button type="button" class="ghost" data-cal="today"${calendarAllowed(today(), input) ? "" : " disabled"}>Today</button><button type="button" class="ghost" data-cal="clear"${input.required ? " disabled" : ""}>Clear</button><button type="button" class="out" data-cal="close">Cancel</button></div>`;
  positionDateCalendar();
  if (focusDay) {
    const day = panel.querySelector(
      `[data-cal-day="${state.focus}"]:not(:disabled)`,
    );
    (day || panel.querySelector('[data-cal="close"]')).focus();
  }
}

function openDateCalendar(input) {
  if (input.disabled || input.readOnly) return;
  closeDateCalendar();
  let value = calendarDate(input.value) ? input.value : today();
  if (input.min && value < input.min) value = input.min;
  if (input.max && value > input.max) value = input.max;
  const date = calendarDate(value) || calendarDate(today());
  dateCalendar = {
    input,
    month: calendarMonth(date.getUTCFullYear(), date.getUTCMonth()),
    focus: calendarISO(date),
  };
  renderDateCalendar(true);
}

function chooseCalendarDate(value) {
  const state = dateCalendar;
  if (!state || (value && !calendarAllowed(value, state.input))) return;
  const input = state.input,
    id = input.id,
    changed = input.value !== value;
  closeDateCalendar();
  input.value = value;
  if (changed) {
    input.dispatchEvent(new Event("input", { bubbles: true }));
    input.dispatchEvent(new Event("change", { bubbles: true }));
  }
  const next = document.getElementById(id) || input;
  if (next.isConnected) next.focus();
}

function dateCalendarClick(ev) {
  const target = ev.target,
    field = target.closest?.('input[type="date"]');
  if (field) {
    ev.preventDefault();
    if (dateCalendar?.input === field) closeDateCalendar(true);
    else openDateCalendar(field);
    return true;
  }
  if (!dateCalendar) return false;
  const panel = target.closest?.("#date-calendar");
  if (!panel) {
    closeDateCalendar();
    return false;
  }
  const b = target.closest("button");
  if (!b || b.disabled) return true;
  ev.preventDefault();
  if (b.dataset.calDay) {
    chooseCalendarDate(b.dataset.calDay);
    return true;
  }
  const act = b.dataset.cal;
  if (act === "today") chooseCalendarDate(today());
  else if (act === "clear") chooseCalendarDate("");
  else if (act === "close") closeDateCalendar(true);
  else if (act === "prev" || act === "next") {
    const m = dateCalendar.month;
    dateCalendar.month = calendarMonth(
      m.getUTCFullYear(),
      m.getUTCMonth() + (act === "prev" ? -1 : 1),
    );
    dateCalendar.focus = calendarISO(dateCalendar.month);
    renderDateCalendar();
    document.querySelector(`#date-calendar [data-cal="${act}"]`)?.focus();
  }
  return true;
}

function dateCalendarKey(ev) {
  if (
    ev.target.matches?.('input[type="date"]') &&
    (ev.key === " " || (ev.altKey && ev.key === "ArrowDown"))
  ) {
    ev.preventDefault();
    return openDateCalendar(ev.target);
  }
  if (!dateCalendar) return;
  if (ev.key === "Escape") {
    ev.preventDefault();
    closeDateCalendar(true);
    return;
  }
  const panel = document.getElementById("date-calendar");
  if (!panel?.contains(ev.target)) return;
  if (ev.key === "Tab") {
    const controls = [
        ...panel.querySelectorAll(
          'button:not(:disabled):not([tabindex="-1"]),select',
        ),
      ],
      first = controls[0],
      last = controls.at(-1);
    if (ev.shiftKey && ev.target === first) {
      ev.preventDefault();
      last.focus();
    } else if (!ev.shiftKey && ev.target === last) {
      ev.preventDefault();
      first.focus();
    }
    return;
  }
  if (!ev.target.dataset.calDay) return;
  const value = ev.target.dataset.calDay,
    date = calendarDate(value);
  let next;
  const delta = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };
  if (ev.key in delta) next = addDays(value, delta[ev.key]);
  else if (ev.key === "Home") next = addDays(value, -date.getUTCDay());
  else if (ev.key === "End") next = addDays(value, 6 - date.getUTCDay());
  else if (ev.key === "PageUp" || ev.key === "PageDown") {
    const m = calendarMonth(
        date.getUTCFullYear(),
        date.getUTCMonth() + (ev.key === "PageUp" ? -1 : 1),
      ),
      last = calendarMonth(m.getUTCFullYear(), m.getUTCMonth() + 1);
    next = addDays(
      calendarISO(m),
      Math.min(date.getUTCDate(), +addDays(calendarISO(last), -1).slice(-2)) -
        1,
    );
  }
  if (next && calendarDate(next)) {
    ev.preventDefault();
    if (!calendarAllowed(next, dateCalendar.input)) return;
    dateCalendar.focus = next;
    const d = calendarDate(next);
    dateCalendar.month = calendarMonth(d.getUTCFullYear(), d.getUTCMonth());
    renderDateCalendar(true);
  }
}

export {
  dateCalendar,
  calendarISO,
  calendarDate,
  calendarMonth,
  calendarDays,
  calendarAllowed,
  closeDateCalendar,
  positionDateCalendar,
  renderDateCalendar,
  openDateCalendar,
  chooseCalendarDate,
  dateCalendarClick,
  dateCalendarKey,
};
